import { useState, useRef } from 'react';
import { api } from '../lib/api';

interface AudioPlayerProps {
  recordingId: string;
}

export function AudioPlayer({ recordingId }: AudioPlayerProps) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const handlePlay = async () => {
    if (isPlaying && audioRef.current) {
      audioRef.current.pause();
      setIsPlaying(false);
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      if (!audioRef.current) {
        const audioUrl = api.getRecordingUrl(recordingId);
        const response = await fetch(audioUrl, {
          headers: {
            'Authorization': `Bearer ${api.getToken()}`
          }
        });

        if (!response.ok) {
          throw new Error('Failed to load audio');
        }

        const blob = await response.blob();
        const url = URL.createObjectURL(blob);

        audioRef.current = new Audio(url);
        audioRef.current.onended = () => setIsPlaying(false);
        audioRef.current.onerror = () => {
          setError('Failed to play audio');
          setIsPlaying(false);
        };
      }

      await audioRef.current.play();
      setIsPlaying(true);
    } catch (err: any) {
      setError(err.message || 'Failed to play audio');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex items-center gap-3">
      <button
        onClick={handlePlay}
        disabled={isLoading}
        className="btn btn-secondary"
        style={{ padding: '0.5rem 1rem', fontSize: '0.875rem' }}
      >
        {isLoading ? (
          <span className="animate-spin">⏳</span>
        ) : isPlaying ? (
          <>⏸️ Pause</>
        ) : (
          <>▶️ Play</>
        )}
      </button>
      {error && <span className="text-red-500 text-sm">{error}</span>}
    </div>
  );
}
